import { useState, useRef, forwardRef } from 'react';
import CollapsedCard from './CollpasedCard';
import CardBack from './CardBack';
import { NameFit } from './RowCard';
import { IsMobile } from '../utils/Mobile';
const { Card, Overlay, Modal } = require('react-bootstrap');
const { Age, AgeOrder } = require('@tta/engine/Constants/Constants')
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');
const { GetCardColor, GetOpposedTextColor } = require('./Utilities/Utilities');

const CardCollectionTooltip = forwardRef(({arrowProps, hasDoneInitialMeasure, ...rest}, ref) => {
    return <div ref={ref}{...rest} />
});

// A card lying face up on a deck, as a card in the row would look
export const DeckTop = ({card}) =>
    <div className="tta-deck-top" style={{ backgroundColor: GetCardColor(card) }}>
        <div className="tta-deck-top-band">{card.age.substring(4)}</div>
        <div className="tta-deck-top-body" style={NameFit(card.name)}>{card.name}</div>
    </div>;

// The kinds of card in the order the deck lists show them
const TypeOrder = ['Leader', 'Wonder', 'Technology', 'Action', 'Tactic', 'Aggression', 'War', 'Pact', 'Event', 'Territory', 'Bonus'];
const TypeLabels = { Leader: 'Leaders', Wonder: 'Wonders', Technology: 'Technologies', Action: 'Actions', Tactic: 'Tactics',
    Aggression: 'Aggressions', War: 'Wars', Pact: 'Pacts', Event: 'Events', Territory: 'Territories', Bonus: 'Defense / Colonization' };

// A deck: its back with the age of its top card (none when empty), and, when it is known, the card lying face up on it.
//   Events (the current and future events): hovering shows the cards in rows by age, each row's known cards (e.g. the
//     viewer's own prepared events) before its face-down ones; Top (seen with Joan of Arc) is called out on its own.
//     Known: cards the viewer knows are among the face-down ones (the current events' prepared ones), shown in their place.
//   Otherwise (the civil and military decks): clicking opens a list of the cards it holds, or for a face-down deck
//     (Candidates: [{ type, name, age, count }]) those it could hold, each copy as a card in hand (hover for the card).
//     InOrder (looking back over a finished game, everything known): the cards in their order instead, as OrderNote says
// Label: the deck's name, for the list's title
const DeckWithHover = ({deck, deckColor, Candidates, Top, Known = [], Backs = false, InOrder = false, OrderNote, Label}) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [showList, setShowList] = useState(false);
    const componentRef = useRef(null);

    const cardStyle = {
        backgroundColor: deckColor,
        padding: 0,
        width: '80px',
        height: '77px',
        position: 'relative',
        boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.5)',
        fontFamily: 'Stencil, Times New Roman, Serif',
        border: '0px',
        fontSize: '40px',
        color: '#fff',
        textShadow: '0px 0px 4px #e44',
        textAlign:'center',
        justifyContent: 'center',
        cursor: Backs ? 'default' : 'pointer',
    }

    const divStyle = {
        backgroundColor: '#fdf9dc',
        borderRadius: '10px',
        outline: '1px solid ' + deckColor,
        padding: '10px',
        width: '200px',
        zIndex: 1090,
        // (fixed from its first frame: see CardOverlay)
        position: 'fixed',
    }

    // An empty deck shows no age
    var earliestAge = deck.length === 0 ? "" : Age.Age3;
    deck.forEach(card => {
        if (card.age < earliestAge)
        {
            earliestAge = card.age;
        }
    });

    return (
        <div>
            <Card style={cardStyle}
                ref = {componentRef}
                title = {Backs ? undefined : `${Label}: click to see its cards`}
                onClick = {Backs && !IsMobile ? undefined : () => setShowList(true)}
                onMouseEnter = {Backs && !IsMobile ? () => setShowOverlay(true) : undefined}
                onMouseLeave = {Backs && !IsMobile ? () => setShowOverlay(false) : undefined}
            >
                {earliestAge.substring(4)}
                {Top && <DeckTop card={Top}/>}
            </Card>
            {Backs && <Overlay
                show={showOverlay}
                target={componentRef.current}
                placement="bottom"
                // Against the window, so the list never makes the page taller (see CardOverlay)
                flip
                popperConfig={{ strategy: 'fixed' }}
            >
                <CardCollectionTooltip
                    style={divStyle}
                    onMouseEnter = {() => setShowOverlay(true)}
                    ref = {componentRef}
                    onMouseLeave = {() => setShowOverlay(false)}
                >
                    {InOrder ? <EventsInOrder deck={deck} Note={OrderNote}/> : <EventRows deck={deck} Top={Top} Known={Known}/>}
                </CardCollectionTooltip>
            </Overlay>}
            {showList && !Backs && <DeckList Label={Label} deck={deck} Candidates={Candidates} OnClose={() => setShowList(false)}/>}
            {/* (on a phone, which has no hover: the events' rows open on a tap instead) */}
            {showList && Backs && <Modal show onHide={() => setShowList(false)} centered>
                <Modal.Header closeButton><Modal.Title>{Label}</Modal.Title></Modal.Header>
                <Modal.Body className="tta tta-left">{InOrder ? <EventsInOrder deck={deck} Note={OrderNote}/> : <EventRows deck={deck} Top={Top} Known={Known}/>}</Modal.Body>
            </Modal>}
        </div>
    )
}

// The current or future events: the top card when known (Joan of Arc), then a row per age, top age first: the known
// cards (as cards in hand), then the face-down ones (their backs). Known cards take the place of face-down ones of their
// age (the top card, when it is one of them, is already shown). Only the top card's place is known: a footnote says
// the others' order means nothing.
function EventRows({deck, Top, Known = []})
{
    if (deck.length === 0)
    {
        return <div className="tta-muted">None</div>;
    }
    const rest = Top ? deck.filter(card => card !== Top) : deck;
    const topAt = Top ? Known.findIndex(card => card.name === Top.name && card.age === Top.age) : -1;
    const extra = Known.filter((card, index) => index !== topAt);
    const rows = AgeOrder.filter(age => rest.some(card => card.age === age)).map(age => {
        const cards = rest.filter(card => card.age === age);
        const facedown = cards.filter(card => card.type === 'Hidden');
        const told = extra.filter(card => card.age === age).slice(0, facedown.length);
        return { age, known: cards.filter(card => card.type !== 'Hidden').concat(told), hidden: facedown.slice(told.length) };
    });
    return <div className="tta-deck-rows">
        {Top && <div className="tta-deck-row tta-deck-row-top">
            <div className="tta-deck-row-title">Top card</div>
            <CollapsedCard card={Top}/>
        </div>}
        {rows.map(({age, known, hidden}) =>
            <div key={age} className="tta-deck-row">
                {known.map(card => <CollapsedCard key={card.id} card={card}/>)}
                {hidden.length > 0 && <div className="tta-card-backs">{hidden.map((card, index) => <CardBack key={index} age={card.age} title={card.age}/>)}</div>}
            </div>)}
        {rows.some(row => row.known.length > 0) && <div className="tta-muted" style={{ fontSize: '0.75em' }}>* Displayed events do not indicate order</div>}
    </div>;
}

// The current or future events in their order, every one known (a finished game looked back over), numbered, with
// what the order means (Note)
function EventsInOrder({deck, Note})
{
    if (deck.length === 0)
    {
        return <div className="tta-muted">None</div>;
    }
    return <div className="tta-deck-rows">
        {Note && <div className="tta-deck-row-title">{Note}</div>}
        <ol className="tta-deck-order">
            {deck.map(card => <li key={card.id}><CollapsedCard card={card}/></li>)}
        </ol>
    </div>;
}

// A leader or wonder not revealed yet (a secret mix's, or a later age's), in its place among the leaders or wonders
export const UnknownCard = ({card}) => {
    const kind = { type: card.of, deck: 'Civil' };
    return <div className="tta-unknown-card" style={{ backgroundColor: GetCardColor(kind), color: GetOpposedTextColor(kind) }}
        title="Not revealed yet">
        {card.age.substring(4)} - {card.name}
    </div>;
};

// The civil or military deck's cards, larger and ordered (by kind, then name), each copy as a card in hand: what it
// holds, or for a face-down deck what it could hold (Candidates). Leaders and wonders not revealed yet are placeholders
// among the others of their kind.
function DeckList({Label, deck, Candidates, OnClose})
{
    const unknown = deck.filter(card => card.type === 'Hidden' && card.of);
    const facedown = deck.length > 0 && unknown.length === 0 && deck.every(card => card.type === 'Hidden');
    let id = -800000;
    const cards = facedown
        ? (Candidates ?? []).flatMap(entry => Array.from({ length: entry.count }, () => CardConverter({ type: entry.type, name: entry.name, age: entry.age, id: id-- })))
            .filter(card => card)
        : deck.filter(card => card.type !== 'Hidden');
    const hidden = facedown ? 0 : deck.length - cards.length - unknown.length;
    const types = TypeOrder.filter(type => cards.some(card => card.type === type) || unknown.some(card => card.of === type))
        .concat([...new Set(cards.map(card => card.type))].filter(type => !TypeOrder.includes(type)));
    const note = facedown
        ? `${deck.length} card${deck.length === 1 ? '' : 's'} face down. These could be among them: this age's military cards you have not seen elsewhere.`
        : `${deck.length} card${deck.length === 1 ? '' : 's'} left.`;
    return <Modal show onHide={OnClose} centered size="lg" dialogClassName="tta-deck-list-dialog">
        <Modal.Header closeButton>
            <Modal.Title>{Label}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tta tta-left">
            <div className="tta-muted" style={{ marginBottom: '10px' }}>{note}</div>
            {cards.length === 0 && !hidden && <div className="tta-muted">Nothing to show.</div>}
            {types.map(type => {
                const ofType = cards.filter(card => card.type === type).sort((a, b) => a.name.localeCompare(b.name));
                const unknownOfType = unknown.filter(card => card.of === type);
                return <div key={type} className="tta-deck-list-section">
                    <div className="tta-deck-list-title">{TypeLabels[type] ?? type} <span className="tta-muted">{ofType.length + unknownOfType.length}</span></div>
                    <div className="tta-deck-list-cards">
                        {ofType.map((card, index) => <CollapsedCard key={`${card.id}-${index}`} card={card}/>)}
                        {unknownOfType.map(card => <UnknownCard key={card.id} card={card}/>)}
                    </div>
                </div>;
            })}
            {hidden > 0 && <div className="tta-deck-list-section">
                <div className="tta-deck-list-title">Face down <span className="tta-muted">{hidden}</span></div>
            </div>}
        </Modal.Body>
    </Modal>;
}

export default DeckWithHover;
